/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

interface PendingInviteContextType {
  pendingInviteToken: string | null;
  /**
   * Queue an invite token to be picked up by AddFriendConfirmSheet. Passing
   * null clears the queue (e.g. after the sheet is shown or the user signs
   * out). Callers:
   *   - useDeepLinks (universal link / launch URL)
   *   - ScanQRSheet (QR code path)
   */
  setPendingInviteToken: (token: string | null) => void;
}

const PendingInviteContext = createContext<PendingInviteContextType | null>(null);

export function usePendingInvite(): PendingInviteContextType {
  const ctx = useContext(PendingInviteContext);
  if (!ctx)
    throw new Error("usePendingInvite must be used within PendingInviteProvider");
  return ctx;
}

export function PendingInviteProvider({ children }: { children: ReactNode }) {
  const [pendingInviteToken, setToken] = useState<string | null>(null);
  const setPendingInviteToken = useCallback((token: string | null) => {
    setToken(token);
  }, []);

  return (
    <PendingInviteContext.Provider
      value={{ pendingInviteToken, setPendingInviteToken }}
    >
      {children}
    </PendingInviteContext.Provider>
  );
}
